import { Button, Flex, Heading, Text } from '@radix-ui/themes';
import { useCallback, useEffect, useState } from 'react';
import { TbMail } from 'react-icons/tb';
import { Header } from '~/components/Header/Header';
import { authBaseUrl, useSession } from '~/features/auth/context/Session';
import { mustVerifyEmail } from '~/lib/ory/emailVerification';
import layoutStyles from '~/styles/routes.module.scss';
import { getBaseUrl } from '~/utils/getBaseUrl';
import styles from './email-verification-overlay.module.scss';

const REDIRECT_SECONDS = 3;

export function EmailVerificationOverlay() {
  const { session } = useSession();
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  const needsVerification = !!session && mustVerifyEmail(session);

  const redirectToVerify = useCallback(() => {
    window.location.href = `${getBaseUrl(authBaseUrl)}/verify?return_to=${encodeURIComponent(window.location.href)}`;
  }, []);

  useEffect(() => {
    if (!needsVerification) {
      return;
    }

    const timeoutId = window.setTimeout(redirectToVerify, REDIRECT_SECONDS * 1000);
    const intervalId = window.setInterval(() => {
      setSecondsLeft((remaining) => Math.max(0, remaining - 1));
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [needsVerification, redirectToVerify]);

  if (!session || !needsVerification) {
    return null;
  }

  const email = session.identity?.traits?.email as string | undefined;

  return (
    <div className={styles.Page} role="alertdialog" aria-modal="true" aria-labelledby="email-verification-title">
      <Header disableLink className={layoutStyles.LayoutHeader} />
      <div className={styles.Main}>
        <Flex className={styles.Card} direction="column" align="center" gap="4">
          <div className={styles.IconWrap} aria-hidden="true">
            <TbMail size="1.75rem" />
          </div>
          <Flex direction="column" align="center" gap="2">
            <Heading id="email-verification-title" size="6" weight="medium">
              Verify your email
            </Heading>
            <Text size="2" color="gray">
              {email ? (
                <>
                  You need to verify <span className={styles.Email}>{email}</span> before you can continue to Versori.
                </>
              ) : (
                'You need to verify your email before you can continue to Versori.'
              )}
            </Text>
          </Flex>
          <div className={styles.ProgressTrack} aria-hidden="true">
            <div className={styles.ProgressBar} />
          </div>
          <Text size="1" color="gray">
            Redirecting in {secondsLeft} second{secondsLeft === 1 ? '' : 's'}…
          </Text>
          <Button size="3" onClick={redirectToVerify}>
            Continue
          </Button>
        </Flex>
      </div>
    </div>
  );
}
